import { create } from 'zustand';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useSettings } from './SettingsContext';
import { API_CONFIG } from './api';

interface CapsifiModel {
  id: string;
  label: string;
  description?: string;
}

interface TokenState {
  accessToken: string | null;
  expiresAt: number | null;
  isRefreshing: boolean;
  refreshError: string | null;
  selectedModelId: string | null;
  setToken: (token: string, expiresIn: number) => void;
  clearToken: () => void;
  setRefreshing: (refreshing: boolean, error?: string | null) => void;
  setSelectedModel: (modelId: string) => void;
}

export const useTokenStore = create<TokenState>((set) => ({
  accessToken: null,
  expiresAt: null,
  isRefreshing: false,
  refreshError: null,
  selectedModelId: null,
  setToken: (token: string, expiresIn: number) => 
    set({ 
      accessToken: token, 
      expiresAt: Date.now() + (expiresIn * 1000),
      isRefreshing: false,
      refreshError: null
    }),
  clearToken: () => set({ accessToken: null, expiresAt: null }),
  setRefreshing: (refreshing: boolean, error = null) => set({ isRefreshing: refreshing, refreshError: error }),
  setSelectedModel: (modelId: string) => set({ selectedModelId: modelId })
}));

interface TokenContextType {
  selectedModelId: string | null;
  setSelectedModelId: (id: string) => void;
  models: CapsifiModel[];
  isLoading: boolean;
  error: string | null;
  fetchModels: () => Promise<void>;
  refreshTokenIfNeeded: () => Promise<boolean>;
  tokenStatus: { isValid: boolean; timeRemaining: number; isRefreshing: boolean };
}

const TokenContext = createContext<TokenContextType | undefined>(undefined);

export function TokenProvider({ children }: { children: React.ReactNode }) {
  const [selectedModelId, setSelectedModelId] = useState<string | null>(null);
  const [models, setModels] = useState<CapsifiModel[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { apiSettings, updateApiSettings } = useSettings();
  
  // Initialize token manager
  useEffect(() => {
    // Initialize the token manager with the current token if available
    if (apiSettings.token && apiSettings.tokenExpiry) {
      const expiresIn = Math.max(0, Math.floor((apiSettings.tokenExpiry - Date.now()) / 1000));
      if (expiresIn > 0) {
        tokenManager.initializeWithToken(apiSettings.token, expiresIn);
      }
    }
  }, []);

  // Silent token refresh logic
  const refreshTokenIfNeeded = async (): Promise<boolean> => {
    if (!apiSettings.clientId || !apiSettings.clientSecret || !apiSettings.environment) {
      console.debug('Missing API settings, cannot refresh token');
      return false;
    }

    const tokenStatus = tokenManager.getTokenStatus();
    
    // If token is still valid or already refreshing, we're good
    if ((tokenStatus.isValid && tokenStatus.timeRemaining > 30000) || tokenStatus.isRefreshing) {
      return true;
    }

    // Otherwise refresh in the background
    console.debug('Token expired or expiring soon, refreshing silently...');
    try {
      const store = useTokenStore.getState();
      store.setRefreshing(true);
      
      const response = await fetch(`${API_CONFIG.baseUrl}/capsifi/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          clientId: apiSettings.clientId,
          clientSecret: apiSettings.clientSecret,
          environment: apiSettings.environment,
          gatewayApiUrl: apiSettings.gatewayApiUrl
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to refresh token');
      }

      const data = await response.json();
      
      // Update the token store
      store.setToken(data.access_token, data.expires_in);
      
      // Also update the settings context
      updateApiSettings({
        token: data.access_token,
        tokenExpiry: Date.now() + (data.expires_in * 1000)
      });
      
      console.debug('Token successfully refreshed in the background');
      return true;
    } catch (error) {
      console.error('Silent token refresh failed:', error);
      
      // Set refresh error but don't clear token yet - let the user continue with expired token
      // until their next API call, which might trigger the onboarding if really needed
      const store = useTokenStore.getState();
      store.setRefreshing(false, error instanceof Error ? error.message : 'Unknown error');
      return false;
    }
  };

  const fetchModels = async () => {
    if (!apiSettings.clientId || !apiSettings.clientSecret || !apiSettings.environment) {
      setError('Please configure your API settings first');
      return;
    }

    // Try to refresh token if needed before fetching models
    const tokenRefreshed = await refreshTokenIfNeeded();
    if (!tokenRefreshed) {
      console.debug('Could not refresh token, but will try to fetch models anyway');
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/capsifi/models');
      if (!response.ok) {
        // If unauthorized, try one more token refresh before giving up
        if (response.status === 401) {
          const emergencyRefresh = await refreshTokenIfNeeded();
          if (emergencyRefresh) {
            // Try again with fresh token
            const retryResponse = await fetch('/api/capsifi/models');
            if (!retryResponse.ok) {
              throw new Error('Failed to fetch models even after token refresh');
            }
            const data = await retryResponse.json();
            setModels(data);
            return;
          }
        }
        
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch models');
      }

      const data = await response.json();
      setModels(data);
    } catch (err) {
      console.error('Error fetching models:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch models');
    } finally {
      setIsLoading(false);
    }
  };

  // Start the token refresh monitoring when the provider mounts
  useEffect(() => {
    // Initialize token background refresh monitoring
    const interval = setInterval(() => {
      const tokenStatus = tokenManager.getTokenStatus();
      
      // If token is expiring soon (less than 2 minutes) and not already refreshing, refresh it
      if (tokenStatus.isValid && tokenStatus.timeRemaining < 120000 && !tokenStatus.isRefreshing) {
        refreshTokenIfNeeded();
      }
    }, 30000); // Check every 30 seconds
    
    return () => clearInterval(interval);
  }, []);

  return (
    <TokenContext.Provider
      value={{
        selectedModelId,
        setSelectedModelId,
        models,
        isLoading,
        error,
        fetchModels,
        refreshTokenIfNeeded,
        tokenStatus: tokenManager.getTokenStatus()
      }}
    >
      {children}
    </TokenContext.Provider>
  );
}

export function useTokenContext() {
  const context = useContext(TokenContext);
  if (context === undefined) {
    throw new Error('useTokenContext must be used within a TokenProvider');
  }
  return context;
}

class TokenManager {
  private static instance: TokenManager;
  private refreshTimeout: NodeJS.Timeout | null = null;
  private readonly REFRESH_BUFFER = 300; // Refresh 5 minutes before expiry instead of 2 minutes
  private refreshPromise: Promise<boolean> | null = null;

  private constructor() {
    // Initialize and start monitoring token expiration
    this.startExpirationMonitoring();
  }

  public static getInstance(): TokenManager {
    if (!TokenManager.instance) {
      TokenManager.instance = new TokenManager();
    }
    return TokenManager.instance;
  }

  private async refreshToken(): Promise<boolean> {
    // If we're already refreshing, return the existing promise
    if (this.refreshPromise) {
      return this.refreshPromise;
    }
    
    const store = useTokenStore.getState();
    if (store.isRefreshing) return Promise.resolve(false);

    // Set up the refresh promise
    this.refreshPromise = new Promise(async (resolve) => {
      try {
        store.setRefreshing(true);
        
        // Note: We don't make API calls here anymore - this is handled by the 
        // TokenProvider's refreshTokenIfNeeded method which has access to React context
        
        console.debug('Token refresh requested from TokenManager');
        
        // Reset the refresh promise after a delay to allow for new refresh attempts
        setTimeout(() => {
          this.refreshPromise = null;
        }, 5000);
        
        resolve(true);
      } catch (error) {
        console.error('Error refreshing token in TokenManager:', error);
        store.setRefreshing(false, error instanceof Error ? error.message : 'Unknown error');
        
        // Reset the refresh promise after a delay to allow for new refresh attempts
        setTimeout(() => {
          this.refreshPromise = null;
        }, 5000);
        
        resolve(false);
      }
    });
    
    return this.refreshPromise;
  }

  public getTokenStatus(): { isValid: boolean; timeRemaining: number; isRefreshing: boolean } {
    const store = useTokenStore.getState();
    const token = store.accessToken;
    const expiresAt = store.expiresAt;
    const isRefreshing = store.isRefreshing;
    
    if (!token || !expiresAt) {
      return { isValid: false, timeRemaining: 0, isRefreshing };
    }
    
    const now = Date.now();
    const timeRemaining = expiresAt - now;
    
    return {
      isValid: timeRemaining > 0,
      timeRemaining,
      isRefreshing
    };
  }

  private startExpirationMonitoring(): void {
    // Create an interval that checks token expiration status
    setInterval(() => {
      const status = this.getTokenStatus();
      
      // Only log if we have a valid token that's expiring soon
      if (status.isValid && status.timeRemaining < 300000) {
        console.debug(`Token expires in ${(status.timeRemaining / 1000).toFixed(3)} seconds.`);
        
        // Don't automatically refresh through TokenManager anymore
        // This will happen through the TokenProvider's context instead
      }
    }, 30000); // Check every 30 seconds
  }

  public initializeWithToken(token: string, expiresIn: number): void {
    const store = useTokenStore.getState();
    store.setToken(token, expiresIn);
  }
}

export const tokenManager = TokenManager.getInstance(); 